// Поддельные данные карточек и источник поверх них.
//
// ЗДЕСЬ НЕТ НИ ОДНОГО БОЕВОГО ЧИСЛА. Всё, что лежит ниже, придумано: названия судов,
// номера, суммы, фамилии, ИНН. Ни одна цифра отсюда не является замером и не должна
// попасть ни в отчёт, ни в разговор с владельцем. Совпадения с настоящими контрагентами
// случайны — данные собраны так, чтобы на них было видно устройство карточки, а не чтобы
// они что-то утверждали о зеркале.
//
// ЗАЧЕМ ЭТОТ ФАЙЛ. Своей схемы ещё нет, её пишут параллельно. Ждать её значило бы не
// начинать вовсе: карточка — это форма, порядок полей, подстановки, журнал и обратные
// связи, и всё это проверяется без единого запроса. Источник — тот же интерфейс, что
// будет у BFF (`source.ts`), поэтому подмена транспорта не заденет ни один экран.
//
// ЧТО ЗДЕСЬ ВАЖНО, А НЕ УКРАШЕНИЕ.
//   • Записи СВЯЗАНЫ по-настоящему: сделка ссылается на живущие здесь компанию, контакт,
//     судно и договор, счета и акты — на сделку, проверки — на судно. Иначе обратные связи
//     показывали бы пустоту, и «связей нет» нельзя было бы отличить от «связи не работают».
//   • IMO у каждого судна с ВЕРНОЙ контрольной цифрой. Поддельный номер, не сходящийся по
//     контрольной сумме, заставил бы форму краснеть на собственных данных — и первое, чему
//     научился бы человек, это не верить красному.
//   • Ревизия проверяется по-настоящему: разошлась — источник бросает `РевизияРазошлась`,
//     а не пишет молча поверх. Ради этого поведения интерфейс и заводили.
//   • Ни одного таймера. Задержка «как в жизни» превратила бы каждую проверку в ожидание.

import type { ИсточникКарточек, ИтогСохранения, СтрокаСвязи } from './source'
import { РевизияРазошлась } from './source'
import type {
  ЗаписьЖурнала,
  ЗаписьКарточки,
  Значения,
  Событие,
  Состояние,
  Сущность,
  Файл,
  Ярлык,
} from './types'
import { описание } from './specs'
import { подписьСтадии } from './specs/obshchee'

// ─────────────────────────── мелкие сборщики значений ───────────────────────────

const ярл = (сущность: Сущность, id: string, название: string, подсказка?: string): Ярлык =>
  ({ сущность, id, название, подсказка })

const руб = (сумма: number) => ({ сумма, валюта: 'RUB' })

/**
 * Состояние документа кладётся ещё и в значения.
 *
 * Иначе условная обязательность номера («обязателен при выпуске») не сработает: функция
 * обязательности видит только значения. То же самое обязан делать и BFF, когда встанет на
 * место этого файла, — иначе черновик документа завести будет нельзя.
 */
const выпущенныйДок = { doc_state: 'issued' }
const черновикДок = { doc_state: 'draft' }

// ─────────────────────────── компании ───────────────────────────

const КОМПАНИИ: ЗаписьКарточки[] = [
  {
    id: 'c1',
    ревизия: 4,
    состояние: 'active',
    значения: {
      name: 'Балтийская судоходная компания',
      full_name: 'Общество с ограниченной ответственностью «Балтийская судоходная компания»',
      kind: 'customer',
      is_own: false,
      inn: '7801234561',
      kpp: '780101001',
      postal_address: 'Санкт-Петербург, Двинская улица, дом 8, литера А, офис 412',
      email: 'office@baltship.example',
      phone: '+7 812 000-11-22',
      assigned_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
    },
  },
  {
    id: 'c2',
    ревизия: 2,
    состояние: 'active',
    значения: {
      name: 'Северный флот-Сервис',
      full_name: 'Общество с ограниченной ответственностью «Северный флот-Сервис»',
      kind: 'supplier',
      is_own: false,
      inn: '5190345678',
      kpp: '519001001',
      postal_address: 'Мурманск, Портовый проезд, дом 3',
      email: 'zakaz@sevflot.example',
      phone: '+7 815 000-33-44',
      assigned_id: ярл('сотрудник', 'e3', 'Липатов Сергей Николаевич', 'Отдел сервиса СПб'),
    },
  },
  {
    id: 'c3',
    ревизия: 7,
    состояние: 'active',
    значения: {
      name: 'Связь и Радионавигация СПб',
      full_name: 'Общество с ограниченной ответственностью «Связь и Радионавигация СПб»',
      kind: 'partner',
      is_own: true,
      inn: '7802567890',
      kpp: '780201001',
      postal_address: 'Санкт-Петербург, Кожевенная линия, дом 34',
      email: 'info@cirspb.example',
      phone: '+7 812 000-55-66',
      assigned_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
    },
  },
  {
    id: 'c4',
    ревизия: 1,
    состояние: 'draft',
    значения: {
      name: 'Мурманский траловый порт',
      full_name: '',
      kind: 'customer',
      is_own: false,
      inn: '',
      kpp: '',
      postal_address: '',
      email: 'port@mtp.example',
      phone: '+7 815 000-77-88',
      assigned_id: null,
    },
  },
]

// ─────────────────────────── контакты ───────────────────────────

const КОНТАКТЫ: ЗаписьКарточки[] = [
  {
    id: 'p1',
    ревизия: 3,
    состояние: 'active',
    значения: {
      last_name: 'Кузнецов',
      first_name: 'Игорь',
      middle_name: 'Валентинович',
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      position: 'Старший механик',
      email: 'kuznetsov@baltship.example',
      phone: '+7 921 000-01-02',
    },
  },
  {
    id: 'p2',
    ревизия: 1,
    состояние: 'active',
    значения: {
      last_name: 'Соколова',
      first_name: 'Марина',
      middle_name: 'Петровна',
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      position: 'Снабженец',
      email: 'sokolova@sevflot.example',
      phone: '+7 921 000-03-04',
    },
  },
  {
    id: 'p3',
    ревизия: 1,
    состояние: 'active',
    значения: {
      last_name: 'Ерофеев',
      first_name: 'Павел',
      middle_name: 'Сергеевич',
      company_id: ярл('компания', 'c4', 'Мурманский траловый порт'),
      position: 'Капитан',
      email: '',
      phone: '+7 921 000-05-06',
    },
  },
]

// ─────────────────────────── суда ───────────────────────────
//
// Контрольная цифра IMO у всех четырёх посчитана по тому же правилу, что и в `validate.ts`:
// первые шесть цифр умножаются на веса от семи до двух, последняя цифра суммы — седьмая
// цифра номера. Номер, не сходящийся по контрольной сумме, форма подсветила бы красным
// прямо на этих данных.

const СУДА: ЗаписьКарточки[] = [
  {
    id: 'v1',
    ревизия: 5,
    состояние: 'active',
    значения: {
      name: 'Балтика',
      vessel_type_id: 'general_cargo',
      imo: '9074729',
      mmsi: '273456789',
      call_sign: 'UBAL2',
      flag_id: 'RU',
      home_port: 'Санкт-Петербург',
      owner_company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
    },
  },
  {
    id: 'v2',
    ревизия: 2,
    состояние: 'active',
    значения: {
      name: 'Кола',
      vessel_type_id: 'fishing',
      imo: '9431862',
      mmsi: '273111222',
      call_sign: 'UKOL7',
      flag_id: 'RU',
      home_port: 'Мурманск',
      owner_company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
    },
  },
  {
    id: 'v3',
    ревизия: 1,
    состояние: 'active',
    значения: {
      name: 'Нева Стар',
      vessel_type_id: 'tanker',
      imo: '9210347',
      mmsi: '636019283',
      call_sign: 'A8NV5',
      flag_id: 'LR',
      home_port: 'Монровия',
      owner_company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
    },
  },
  {
    id: 'v4',
    ревизия: 1,
    состояние: 'archived',
    значения: {
      name: 'Печора',
      vessel_type_id: 'tug',
      imo: '8650916',
      mmsi: '273998877',
      call_sign: '',
      flag_id: 'RU',
      home_port: 'Архангельск',
      owner_company_id: ярл('компания', 'c4', 'Мурманский траловый порт'),
    },
  },
]

// ─────────────────────────── сотрудники ───────────────────────────

const СОТРУДНИКИ: ЗаписьКарточки[] = [
  {
    id: 'e1',
    ревизия: 2,
    состояние: 'active',
    значения: {
      last_name: 'Петрова',
      first_name: 'Анна',
      middle_name: 'Сергеевна',
      email: 'a.petrova@cirspb.example',
      phone: '+7 812 000-55-61',
      org_unit_id: 'op_1',
      role: 'manager',
      login: 'a.petrova',
      active: true,
      hired_at: '2021-09-01',
      fired_at: '',
    },
  },
  {
    id: 'e2',
    ревизия: 1,
    состояние: 'active',
    значения: {
      last_name: 'Гаврилов',
      first_name: 'Дмитрий',
      middle_name: 'Игоревич',
      email: 'd.gavrilov@cirspb.example',
      phone: '+7 812 000-55-62',
      org_unit_id: 'otdel_servisa_spb',
      role: 'engineer',
      login: 'd.gavrilov',
      active: true,
      hired_at: '2019-04-15',
      fired_at: '',
    },
  },
  {
    id: 'e3',
    ревизия: 1,
    состояние: 'active',
    значения: {
      last_name: 'Липатов',
      first_name: 'Сергей',
      middle_name: 'Николаевич',
      email: 's.lipatov@cirspb.example',
      phone: '+7 812 000-55-63',
      org_unit_id: 'otdel_servisa_spb',
      role: 'coordinator',
      login: 's.lipatov',
      active: true,
      hired_at: '2022-02-07',
      fired_at: '',
    },
  },
  {
    id: 'e4',
    ревизия: 3,
    состояние: 'archived',
    значения: {
      last_name: 'Морозов',
      first_name: 'Виктор',
      middle_name: 'Ильич',
      email: 'v.morozov@cirspb.example',
      phone: '+7 861 000-55-64',
      org_unit_id: 'otdel_servisa_novorossiysk',
      role: 'engineer',
      login: 'v.morozov',
      active: false,
      hired_at: '2018-06-01',
      fired_at: '2026-01-30',
    },
  },
]

// ─────────────────────────── договоры ───────────────────────────

const ДОГОВОРЫ: ЗаписьКарточки[] = [
  {
    id: 'k1',
    ревизия: 2,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'РД-2025-014',
      kind: 'frame',
      parent_id: null,
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      signed_at: '2025-02-03',
      valid_from: '2025-02-03',
      valid_to: '2026-12-31',
      files: [],
    },
  },
  {
    id: 'k2',
    ревизия: 1,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'РД-2025-041',
      kind: 'frame',
      parent_id: null,
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      signed_at: '2025-06-18',
      valid_from: '2025-07-01',
      valid_to: '',
      files: [],
    },
  },
  {
    id: 'k3',
    ревизия: 1,
    состояние: 'draft',
    значения: {
      ...черновикДок,
      no: '',
      kind: 'single',
      parent_id: null,
      company_id: ярл('компания', 'c4', 'Мурманский траловый порт'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      signed_at: '',
      valid_from: '',
      valid_to: '',
      files: [],
    },
  },
]

// ─────────────────────────── сделки ───────────────────────────

const ФАЙЛЫ_СДЕЛКИ: Файл[] = [
  {
    id: 'f1',
    имя: 'КП Балтика март.pdf',
    размер: 184320,
    роль: 'коммерческое предложение',
    добавил: 'Петрова А. С.',
    когда: '2026-03-04T11:20:00+03:00',
  },
  {
    id: 'f2',
    имя: 'Заявка капитана.pdf',
    размер: 96411,
    роль: 'заявка заказчика',
    добавил: 'Липатов С. Н.',
    когда: '2026-03-02T09:41:00+03:00',
  },
  {
    id: 'f3',
    имя: 'Схема антенн.png',
    размер: 1245184,
    роль: 'схема с борта',
    добавил: 'Гаврилов Д. И.',
    когда: '2026-04-09T16:05:00+03:00',
  },
]

const СДЕЛКИ: ЗаписьКарточки[] = [
  {
    id: 'd1',
    ревизия: 7,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      title: 'Радиодевиация т/х Балтика, март',
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      person_id: ярл('контакт', 'p1', 'Кузнецов Игорь Валентинович', 'Старший механик'),
      pipeline_id: 'obschaya',
      stage_id: 's3',
      deal_type_id: 'servis',
      assigned_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
      amount: руб(1480000),
      amount_is_manual: false,
      fx_rate: 1,
      fx_date: '2026-03-02',
      amount_rub: руб(1480000),
      vat_rate_id: 'nds22',
      opened_at: '2026-03-02',
      closed_at: '2026-04-22',
      vessel_id: ярл('судно', 'v1', 'Балтика', 'IMO 9074729'),
      eta: '2026-04-08',
      etd: '2026-04-12',
      work_start_at: '2026-04-09',
      work_end_at: '2026-04-10',
      berth: 'Санкт-Петербург, причал 4-й район',
      payment_terms: 'Постоплата 30 дней от даты акта',
      delivery_terms: 'Работы на борту, выезд бригады за наш счёт',
      other_terms: 'Доступ на судно согласуется с вахтенным помощником накануне',
      manager_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
      coordinator_id: ярл('сотрудник', 'e3', 'Липатов Сергей Николаевич', 'Отдел сервиса СПб'),
      senior_engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      org_unit_id: 'op_1',
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: ярл('договор', 'k1', 'Договор № РД-2025-014', 'рамочный'),
      no: 'СД-2026-000481',
      lost_reason: '',
      files: ФАЙЛЫ_СДЕЛКИ,
    },
  },
  {
    id: 'd2',
    ревизия: 3,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      title: 'Поставка АРБ на т/х Кола',
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      person_id: ярл('контакт', 'p2', 'Соколова Марина Петровна', 'Снабженец'),
      pipeline_id: 'snabzhenie',
      stage_id: 'won',
      deal_type_id: 'prodazha_tovara',
      assigned_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
      amount: руб(515000),
      amount_is_manual: true,
      fx_rate: 1,
      fx_date: '2026-02-05',
      amount_rub: руб(515000),
      vat_rate_id: 'nds22',
      opened_at: '2026-02-05',
      closed_at: '2026-02-28',
      vessel_id: ярл('судно', 'v2', 'Кола', 'IMO 9431862'),
      eta: '2026-02-17',
      etd: '2026-02-20',
      work_start_at: '2026-02-18',
      work_end_at: '2026-02-19',
      berth: 'Мурманск, причал рыбного порта',
      payment_terms: 'Предоплата 100 %',
      delivery_terms: 'Доставка до борта силами поставщика',
      other_terms: '',
      manager_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
      coordinator_id: null,
      senior_engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      org_unit_id: 'op_1',
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: ярл('договор', 'k2', 'Договор № РД-2025-041', 'рамочный'),
      no: 'СД-2026-000377',
      lost_reason: '',
      files: [],
    },
  },
  {
    id: 'd3',
    ревизия: 2,
    состояние: 'draft',
    значения: {
      ...черновикДок,
      title: 'Освидетельствование т/х Печора',
      company_id: ярл('компания', 'c4', 'Мурманский траловый порт'),
      person_id: ярл('контакт', 'p3', 'Ерофеев Павел Сергеевич', 'Капитан'),
      pipeline_id: 'sir',
      stage_id: 'lose',
      deal_type_id: 'servis',
      assigned_id: ярл('сотрудник', 'e1', 'Петрова Анна Сергеевна', 'ОП №1'),
      amount: руб(240000),
      amount_is_manual: true,
      fx_rate: 1,
      fx_date: '2026-05-12',
      amount_rub: руб(240000),
      vat_rate_id: 'nds22',
      opened_at: '2026-05-12',
      closed_at: '2026-06-01',
      vessel_id: ярл('судно', 'v4', 'Печора', 'IMO 8650916'),
      eta: '',
      etd: '',
      work_start_at: '',
      work_end_at: '',
      berth: '',
      payment_terms: '',
      delivery_terms: '',
      other_terms: '',
      manager_id: null,
      coordinator_id: null,
      senior_engineer_id: null,
      org_unit_id: 'otdel_servisa_novorossiysk',
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: null,
      no: '',
      lost_reason: 'Заказчик выбрал подрядчика в Архангельске: наш выезд из Петербурга дороже самих работ',
      files: [],
    },
  },
]

// ─────────────────────────── счета ───────────────────────────

const СЧЕТА: ЗаписьКарточки[] = [
  {
    id: 'i1',
    ревизия: 2,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'СЧ-2026-000912',
      direction: 'out',
      issued_at: '2026-03-20',
      deal_id: ярл('сделка', 'd1', 'Радиодевиация т/х Балтика, март', 'Отчет'),
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: ярл('договор', 'k1', 'Договор № РД-2025-014', 'рамочный'),
      amount: руб(1480000),
      vat_rate_id: 'nds22',
      vat_amount: руб(266885.25),
      expense_item_id: '',
      due_at: '2026-04-03',
      paid_at: '',
      paid_amount: { сумма: null, валюта: 'RUB' },
      approved_by: null,
      approved_at: '',
    },
  },
  {
    id: 'i2',
    ревизия: 4,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'СЧ-2026-000913',
      direction: 'out',
      issued_at: '2026-02-10',
      deal_id: ярл('сделка', 'd2', 'Поставка АРБ на т/х Кола', 'Сделка заключена'),
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: ярл('договор', 'k2', 'Договор № РД-2025-041', 'рамочный'),
      amount: руб(515000),
      vat_rate_id: 'nds22',
      vat_amount: руб(92868.85),
      expense_item_id: '',
      due_at: '2026-02-24',
      paid_at: '2026-02-21',
      paid_amount: руб(515000),
      approved_by: null,
      approved_at: '',
    },
  },
  {
    id: 'i3',
    ревизия: 1,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'ПС-2026-77',
      direction: 'in',
      issued_at: '2026-03-05',
      deal_id: ярл('сделка', 'd1', 'Радиодевиация т/х Балтика, март', 'Отчет'),
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      own_company_id: ярл('компания', 'c3', 'Связь и Радионавигация СПб', 'наша компания'),
      contract_id: null,
      amount: руб(310000),
      vat_rate_id: 'nds22',
      vat_amount: руб(55901.64),
      expense_item_id: 'deal',
      due_at: '2026-03-19',
      paid_at: '2026-03-18',
      paid_amount: руб(310000),
      approved_by: ярл('сотрудник', 'e3', 'Липатов Сергей Николаевич', 'Отдел сервиса СПб'),
      approved_at: '2026-03-16',
    },
  },
]

// ─────────────────────────── акты ───────────────────────────

const ФАЙЛЫ_АКТА: Файл[] = [
  {
    id: 'f4',
    имя: 'Акт подписанный.pdf',
    размер: 512000,
    роль: 'скан акта',
    добавил: 'Гаврилов Д. И.',
    когда: '2026-04-11T10:02:00+03:00',
  },
  {
    id: 'f5',
    имя: 'Отгрузка на борт.jpg',
    размер: 2458624,
    роль: 'фото отгрузки',
    добавил: 'Гаврилов Д. И.',
    когда: '2026-04-10T14:47:00+03:00',
  },
]

const АКТЫ: ЗаписьКарточки[] = [
  {
    id: 'a1',
    ревизия: 3,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'АВР-2026-0233',
      act_date: '2026-04-10',
      deal_id: ярл('сделка', 'd1', 'Радиодевиация т/х Балтика, март', 'Отчет'),
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      vessel_id: ярл('судно', 'v1', 'Балтика', 'IMO 9074729'),
      engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      place: 'Санкт-Петербург, причал 4-й район',
      works: 'Радиодевиация УКВ-станции основного состава.\nПроверка АИС с записью параметров.\nЗамена антенного кабеля на ходовом мостике.',
      hours_total: 18.5,
      conclusion: 'Оборудование исправно. К следующему выходу заменить крепление антенны АИС — держится на временном хомуте.',
      amount: руб(1480000),
      files: ФАЙЛЫ_АКТА,
    },
  },
  {
    id: 'a2',
    ревизия: 1,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      no: 'АВР-2026-0234',
      act_date: '2026-02-19',
      deal_id: ярл('сделка', 'd2', 'Поставка АРБ на т/х Кола', 'Сделка заключена'),
      company_id: ярл('компания', 'c2', 'Северный флот-Сервис', 'ИНН 5190345678'),
      vessel_id: ярл('судно', 'v2', 'Кола', 'IMO 9431862'),
      engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      place: 'Мурманск, причал рыбного порта',
      works: 'Установка аварийного радиобуя и регистрация в базе опознавания.',
      hours_total: 6,
      conclusion: 'Буй установлен, гидростат в сроке.',
      amount: руб(515000),
      files: [],
    },
  },
  {
    id: 'a3',
    ревизия: 1,
    состояние: 'draft',
    значения: {
      ...черновикДок,
      no: '',
      act_date: '2026-07-24',
      deal_id: ярл('сделка', 'd1', 'Радиодевиация т/х Балтика, март', 'Отчет'),
      company_id: ярл('компания', 'c1', 'Балтийская судоходная компания', 'ИНН 7801234561'),
      vessel_id: ярл('судно', 'v1', 'Балтика', 'IMO 9074729'),
      engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      place: 'Санкт-Петербург, причал 4-й район',
      works: 'Повторный выезд по замечанию к креплению антенны.',
      hours_total: 4,
      conclusion: '',
      amount: { сумма: null, валюта: 'RUB' },
      files: [],
    },
  },
]

// ─────────────────────────── проверки ───────────────────────────

const ПРОВЕРКИ: ЗаписьКарточки[] = [
  {
    id: 's1',
    ревизия: 2,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      kind_id: 'sart',
      vessel_id: ярл('судно', 'v1', 'Балтика', 'IMO 9074729'),
      deal_id: ярл('сделка', 'd1', 'Радиодевиация т/х Балтика, март', 'Отчет'),
      engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      performed_at: '2026-04-09',
      result: 'ok',
      defects_found: 0,
      next_due_at: '2027-04-09',
      reviewed_by: ярл('сотрудник', 'e3', 'Липатов Сергей Николаевич', 'Отдел сервиса СПб'),
      reviewed_at: '2026-04-12',
    },
  },
  {
    id: 's2',
    ревизия: 1,
    состояние: 'issued',
    значения: {
      ...выпущенныйДок,
      kind_id: 'epirb',
      vessel_id: ярл('судно', 'v2', 'Кола', 'IMO 9431862'),
      deal_id: ярл('сделка', 'd2', 'Поставка АРБ на т/х Кола', 'Сделка заключена'),
      engineer_id: ярл('сотрудник', 'e2', 'Гаврилов Дмитрий Игоревич', 'Отдел сервиса СПб'),
      performed_at: '2026-02-18',
      result: 'defect',
      defects_found: 2,
      next_due_at: '2026-08-18',
      reviewed_by: null,
      reviewed_at: '',
    },
  },
  {
    id: 's3',
    ревизия: 1,
    состояние: 'draft',
    значения: {
      ...черновикДок,
      kind_id: 'batt',
      vessel_id: ярл('судно', 'v4', 'Печора', 'IMO 8650916'),
      deal_id: null,
      engineer_id: ярл('сотрудник', 'e4', 'Морозов Виктор Ильич', 'Отдел сервиса Новороссийск'),
      performed_at: '2025-11-12',
      result: 'not_done',
      defects_found: 0,
      next_due_at: '2026-05-12',
      reviewed_by: null,
      reviewed_at: '',
    },
  },
]

// ─────────────────────────── журнал, файлы, лента ───────────────────────────
//
// Журнал главной сделки — самое ценное в этих данных. Он показывает то, ради чего журнал
// вообще открывают: пару «из чего во что», разных людей и смену стадии рядом с правкой
// суммы. Свежее первым — так его и читают.

const ЖУРНАЛ_СДЕЛКИ: ЗаписьЖурнала[] = [
  {
    ревизия: 7,
    когда: '2026-04-16T09:12:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    операция: 'update',
    поле: 'closed_at',
    подписьПоля: 'Плановое завершение',
    из: '15.04.2026',
    во: '22.04.2026',
    откуда: 'ui',
  },
  {
    ревизия: 6,
    когда: '2026-04-10T17:40:00+03:00',
    кто: 'Гаврилов Дмитрий Игоревич',
    операция: 'update',
    поле: 'stage_id',
    подписьПоля: 'Стадия',
    из: 'Отгрузка товара',
    во: 'Отчет',
    откуда: 'ui',
  },
  {
    ревизия: 5,
    когда: '2026-04-02T11:05:00+03:00',
    кто: 'Липатов Сергей Николаевич',
    операция: 'update',
    поле: 'senior_engineer_id',
    подписьПоля: 'Старший инженер',
    из: '—',
    во: 'Гаврилов Дмитрий Игоревич',
    откуда: 'ui',
  },
  {
    ревизия: 4,
    когда: '2026-03-21T08:55:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    операция: 'update',
    поле: 'amount',
    подписьПоля: 'Сумма',
    из: '1 250 000,00 ₽',
    во: '1 480 000,00 ₽',
    откуда: 'ui',
  },
  {
    ревизия: 3,
    когда: '2026-03-12T14:20:00+03:00',
    кто: 'Липатов Сергей Николаевич',
    операция: 'update',
    поле: 'berth',
    подписьПоля: 'Место стоянки',
    из: '—',
    во: 'Санкт-Петербург, причал 4-й район',
    откуда: 'ui',
  },
  {
    ревизия: 2,
    когда: '2026-03-05T10:02:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    операция: 'update',
    поле: 'payment_terms',
    подписьПоля: 'Условия оплаты',
    из: 'Предоплата 100 %',
    во: 'Постоплата 30 дней от даты акта',
    откуда: 'ui',
  },
  {
    ревизия: 1,
    когда: '2026-03-02T09:30:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    операция: 'create',
    откуда: 'ui',
  },
]

const ЖУРНАЛ_СУДНА: ЗаписьЖурнала[] = [
  {
    ревизия: 5,
    когда: '2026-03-30T12:00:00+03:00',
    кто: 'Обмен с зеркалом',
    операция: 'update',
    поле: 'home_port',
    подписьПоля: 'Порт приписки',
    из: 'СПб',
    во: 'Санкт-Петербург',
    откуда: 'job',
  },
  {
    ревизия: 4,
    когда: '2026-01-19T15:33:00+03:00',
    кто: 'Липатов Сергей Николаевич',
    операция: 'update',
    поле: 'flag_id',
    подписьПоля: 'Флаг',
    из: 'РФ',
    во: 'Россия',
    откуда: 'ui',
  },
]

const ЛЕНТА_СДЕЛКИ: Событие[] = [
  {
    id: 'ev1',
    когда: '2026-04-16T09:14:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    вид: 'комментарий',
    текст: 'Заказчик просит сдвинуть подписание акта на конец недели — бухгалтер в отпуске.',
  },
  {
    id: 'ev2',
    когда: '2026-04-11T10:05:00+03:00',
    кто: 'Гаврилов Дмитрий Игоревич',
    вид: 'задача',
    текст: 'Передать подписанный акт в бухгалтерию до конца недели.',
  },
  {
    id: 'ev3',
    когда: '2026-04-10T17:40:00+03:00',
    кто: 'Гаврилов Дмитрий Игоревич',
    вид: 'стадия',
    текст: 'Стадия: «Отгрузка товара» → «Отчет».',
  },
  {
    id: 'ev4',
    когда: '2026-04-08T08:20:00+03:00',
    кто: 'Липатов Сергей Николаевич',
    вид: 'звонок',
    текст: 'Вахтенный помощник подтвердил доступ на борт с утра.',
  },
  {
    id: 'ev5',
    когда: '2026-03-20T16:02:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    вид: 'письмо',
    текст: 'Отправлен счёт СЧ-2026-000912 на адрес office@baltship.example.',
  },
  {
    id: 'ev6',
    когда: '2026-03-04T11:25:00+03:00',
    кто: 'Петрова Анна Сергеевна',
    вид: 'письмо',
    текст: 'Отправлено коммерческое предложение, приложение — КП в PDF.',
  },
]

// ─────────────────────────── база целиком ───────────────────────────

interface База {
  записи: Record<Сущность, ЗаписьКарточки[]>
  журналы: Record<string, ЗаписьЖурнала[]>
  ленты: Record<string, Событие[]>
}

const ОБРАЗЕЦ: База = {
  записи: {
    сделка: СДЕЛКИ,
    компания: КОМПАНИИ,
    контакт: КОНТАКТЫ,
    судно: СУДА,
    сотрудник: СОТРУДНИКИ,
    договор: ДОГОВОРЫ,
    счёт: СЧЕТА,
    акт: АКТЫ,
    проверка: ПРОВЕРКИ,
  },
  журналы: {
    'сделка:d1': ЖУРНАЛ_СДЕЛКИ,
    'судно:v1': ЖУРНАЛ_СУДНА,
  },
  ленты: {
    'сделка:d1': ЛЕНТА_СДЕЛКИ,
  },
}

/** id главной записи каждой сущности — с неё открывается демонстрация. */
export const ПОДДЕЛЬНЫЕ_ID: Record<Сущность, string> = {
  сделка: 'd1',
  компания: 'c1',
  контакт: 'p1',
  судно: 'v1',
  сотрудник: 'e1',
  договор: 'k1',
  счёт: 'i1',
  акт: 'a1',
  проверка: 's1',
}

/**
 * Копия без общей памяти.
 *
 * Через JSON, а не через `structuredClone`: значения карточки — это строки, числа,
 * булевы, деньги, ярлыки и файлы, то есть ровно то, что JSON переживает без потерь.
 * Ни дат-объектов, ни функций здесь нет и быть не может — их не пропустит `Значение`.
 */
const копия = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T

// ─────────────────────────── ярлыки для поиска ───────────────────────────

/** Как запись выглядит в поле-связи и в списке поиска: название плюс чем различать тёзок. */
function ярлыкЗаписи(сущность: Сущность, з: ЗаписьКарточки): Ярлык {
  const в = з.значения
  const s = (код: string) => (typeof в[код] === 'string' ? (в[код] as string) : '')
  const фио = [s('last_name'), s('first_name'), s('middle_name')].filter(Boolean).join(' ')
  switch (сущность) {
    case 'сделка':
      return ярл(сущность, з.id, s('title'), подписьСтадии(s('stage_id')))
    case 'компания':
      return ярл(сущность, з.id, s('name'), s('inn') ? `ИНН ${s('inn')}` : 'ИНН не заполнен')
    case 'контакт':
      return ярл(сущность, з.id, фио, s('position'))
    case 'судно':
      return ярл(сущность, з.id, s('name'), s('imo') ? `IMO ${s('imo')}` : 'IMO не заполнен')
    case 'сотрудник':
      return ярл(сущность, з.id, фио, s('login'))
    case 'договор':
      return ярл(сущность, з.id, s('no') ? `Договор № ${s('no')}` : 'Договор без номера', s('kind'))
    case 'счёт':
      return ярл(сущность, з.id, s('no') ? `Счёт № ${s('no')}` : 'Счёт без номера', s('issued_at'))
    case 'акт':
      return ярл(сущность, з.id, s('no') ? `Акт № ${s('no')}` : 'Акт без номера', s('act_date'))
    default:
      return ярл(сущность, з.id, s('kind_id'), s('performed_at'))
  }
}

// ─────────────────────────── сам источник ───────────────────────────

/**
 * Свежая независимая копия — по одной на тест и на экран, чтобы правки не текли между ними.
 *
 * Общий изменяемый источник однажды уже дал бы самый неприятный вид ложной проверки:
 * набор зелёный по одному, красный целиком, потому что соседний тест успел сохранить
 * карточку. Поэтому данные копируются при каждом вызове.
 */
export function сделатьПоддельныйИсточник(): ИсточникКарточек {
  const база = копия(ОБРАЗЕЦ)

  const найти = (сущность: Сущность, id: string): ЗаписьКарточки | undefined =>
    база.записи[сущность]?.find((з) => з.id === id)

  return {
    async запись(сущность, id) {
      const з = найти(сущность, id)
      // Копия наружу: экран правит значения у себя, и до `сохранить` база их знать не должна.
      return з ? копия(з) : null
    },

    async поиск(сущность, запрос) {
      const q = запрос.trim().toLowerCase()
      // Одна буква — это ещё не запрос: на неё отзовётся половина справочника.
      if (q.length < 2) return []
      return (база.записи[сущность] ?? [])
        .map((з) => ярлыкЗаписи(сущность, з))
        .filter((я) => `${я.название} ${я.подсказка ?? ''}`.toLowerCase().includes(q))
    },

    async журнал(сущность, id) {
      return копия(база.журналы[`${сущность}:${id}`] ?? [])
    },

    async документы(сущность, id) {
      const з = найти(сущность, id)
      const ф = з?.значения.files
      return Array.isArray(ф) ? копия(ф as Файл[]) : []
    },

    async лента(сущность, id) {
      return копия(база.ленты[`${сущность}:${id}`] ?? [])
    },

    async обратные(сущность, id, код) {
      const связь = описание(сущность).обратные.find((о) => о.код === код)
      if (!связь) return []
      const итог: СтрокаСвязи[] = []
      for (const з of база.записи[связь.сущность] ?? []) {
        const v = з.значения[связь.поле]
        // Связь — это ярлык с id, а не «нуль вместо ссылки»: нуля как объекта не бывает.
        if (v && typeof v === 'object' && 'id' in v && (v as Ярлык).id === id) {
          итог.push({ id: з.id, значения: копия(з.значения) })
        }
      }
      return итог
    },

    async сохранить(
      сущность: Сущность,
      id: string,
      значения: Значения,
      ревизия: number,
      состояние?: Состояние,
    ): Promise<ИтогСохранения> {
      const з = найти(сущность, id)
      if (!з) throw new Error(`Записи нет: ${сущность} ${id}`)
      // Разошлась ревизия — значит запись правил кто-то ещё. Молча перезаписать чужое
      // изменение хуже, чем отказать: отказ человек увидит, пропажу — нет.
      if (з.ревизия !== ревизия) throw new РевизияРазошлась(з.ревизия)

      // Слияние, а не замена: форма присылает существующей записи только ИЗМЕНЁННЫЕ поля
      // (так работает и настоящий транспорт), и замена целиком стёрла бы остальное.
      з.значения = { ...з.значения, ...копия(значения) }
      if (состояние) {
        з.состояние = состояние
        // Состояние документа живёт и в значениях — на нём держится условная
        // обязательность номера. Разъедутся эти два места — номер перестанет требоваться.
        if (описание(сущность).документ) з.значения.doc_state = состояние
      }
      з.ревизия += 1
      return { ревизия: з.ревизия, состояние: з.состояние, значения: копия(з.значения) }
    },
  }
}

/**
 * Общий источник для демонстрации.
 *
 * Один на всё приложение — и это нарочно: экран демонстрации должен помнить правки, пока
 * человек ходит по вкладкам. Для проверок он не годится, там нужна своя копия.
 */
export const поддельныйИсточник: ИсточникКарточек = сделатьПоддельныйИсточник()
